import React from 'react';
import { ClockIcon, CheckCircleIcon, XIcon, BookmarkIcon } from './icons/InterfaceIcons';

interface TestInstructionsProps {
    test: {
        name: string;
        duration: number;
        questionsCount: number;
        negativeMarks?: number;
        totalMarks: number;
    };
    onStartTest: () => void;
    onBack: () => void;
    isStartDisabled?: boolean;
    networkTestComponent?: React.ReactNode;
}

export const TestInstructions: React.FC<TestInstructionsProps> = ({ test, onStartTest, onBack, isStartDisabled = false, networkTestComponent }) => {
    const instructions = [
        {
            icon: <ClockIcon className="w-6 h-6 text-blue-500" />,
            title: "Time Management",
            description: `You have ${test.duration} minutes to complete ${test.questionsCount} questions. Plan your time accordingly.`
        },
        {
            icon: <CheckCircleIcon className="w-6 h-6 text-green-500" />,
            title: "Scoring System",
            description: `Each correct answer gives you +4 marks. Total marks: ${test.totalMarks}`
        },
        {
            icon: <XIcon className="w-6 h-6 text-red-500" />,
            title: "Negative Marking",
            description: `Each incorrect answer deducts ${test.negativeMarks || 1} mark. Unattempted questions carry no penalty.`
        },
        {
            icon: <BookmarkIcon className="w-6 h-6 text-purple-500" />,
            title: "Navigation",
            description: "Use the question palette to navigate between questions. You can mark questions for review and return to them later."
        }
    ];

    const guidelines = [
        "Read each question carefully before selecting an answer",
        "You can change your answers anytime before submitting",
        "Use the 'Mark for Review' feature for questions you want to revisit",
        "The timer will be visible at all times during the test",
        "The test will auto-submit when time expires",
        "Ensure stable internet connection throughout the test"
    ];

    return (
        <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
            <div className="w-full max-w-4xl mx-auto">
                <div className="bg-brand-surface border border-brand-border rounded-xl shadow-card p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-brand-text-primary mb-2">Test Instructions</h1>
                        <h2 className="text-xl text-brand-text-secondary">{test.name}</h2>
                        <div className="flex justify-center items-center space-x-6 mt-4 text-sm text-brand-text-secondary">
                            <div className="flex items-center space-x-2">
                                <ClockIcon className="w-4 h-4" />
                                <span>{test.duration} minutes</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span>{test.questionsCount} questions</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span>{test.totalMarks} marks</span>
                            </div>
                        </div>
                    </div>

                    {/* Key Instructions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        {instructions.map((instruction, index) => (
                            <div key={index} className="flex items-start space-x-4 p-4 bg-brand-bg rounded-lg">
                                <div className="flex-shrink-0">
                                    {instruction.icon}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-brand-text-primary mb-1">{instruction.title}</h3>
                                    <p className="text-sm text-brand-text-secondary">{instruction.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {networkTestComponent}

                    {/* General Guidelines */}
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-brand-text-primary mb-4">General Guidelines</h3>
                        <div className="bg-brand-bg rounded-lg p-4">
                            <ul className="space-y-2">
                                {guidelines.map((guideline, index) => (
                                    <li key={index} className="flex items-start space-x-3 text-sm text-brand-text-secondary">
                                        <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0"></div>
                                        <span>{guideline}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Question Palette Legend */}
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-brand-text-primary mb-4">Question Status Legend</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-green-500 text-white rounded flex items-center justify-center text-sm font-semibold">1</div>
                                <span className="text-sm text-brand-text-secondary">Answered</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-red-500 text-white rounded flex items-center justify-center text-sm font-semibold">2</div>
                                <span className="text-sm text-brand-text-secondary">Not Answered</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-purple-500 text-white rounded flex items-center justify-center text-sm font-semibold">3</div>
                                <span className="text-sm text-brand-text-secondary">Marked for Review</span>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-blue-500 text-white rounded flex items-center justify-center text-sm font-semibold">4</div>
                                <span className="text-sm text-brand-text-secondary">Answered & Marked</span>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                        <button 
                            onClick={onBack}
                            className="w-full sm:w-auto px-8 py-3 text-base font-semibold rounded-lg border border-brand-border hover:bg-brand-bg transition-colors"
                        >
                            Back to Dashboard
                        </button>
                        <button 
                            onClick={onStartTest}
                            disabled={isStartDisabled}
                            className={`w-full sm:w-auto px-8 py-3 text-base font-semibold text-white bg-brand-primary rounded-lg transition-colors shadow-sm ${isStartDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-brand-primary-hover'}`}
                        >
                            Start Test
                        </button>
                    </div>

                    {/* Disclaimer */}
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>Important:</strong> Once you start the test, the timer will begin and cannot be paused. 
                            Make sure you have a stable internet connection and are in a distraction-free environment.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};